import { useForm } from '@formspree/react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import SuccessPopup from '../../components/SuccessPopup';
import { useTranslation } from 'react-i18next';

const formVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

export default function QuoteRequest() {
  const { t } = useTranslation();
  const [formState, handleSubmit] = useForm("xbllezdb");
  const [formData, setFormData] = useState({
    conferenceName: '',
    attendeeCount: '',
    eventDate: '',
    name: '',
    email: '',
    requirements: ''
  });

  // Add state for popup
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Watch for form success and show popup
  useEffect(() => {
    if (formState.succeeded) {
      setShowSuccess(true);
      // Reset form data
      setFormData({
        conferenceName: '',
        attendeeCount: '',
        eventDate: '',
        name: '',
        email: '',
        requirements: ''
      });
    }
  }, [formState.succeeded]);

  return (
    <>
      <section id="quote-request" className="relative py-12 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div 
            className="relative"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={formVariants}
          >
            {/* Background decoration */}
            <div className="absolute inset-0 bg-primary/5 rounded-3xl" />
            
            {/* Content grid */}
            <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-24 p-8 lg:p-12">
              {/* Left side - Copy */}
              <motion.div className="space-y-10 lg:py-8" variants={itemVariants}>
                <div className="space-y-5">
                  <h2 className="text-4xl md:text-5xl font-bold text-primary whitespace-pre-line">
                    See Exactly How BridgeOnce Can Transform Your Event Networking
                  </h2>
                  <p className="text-2xl text-text/80 leading-relaxed">
                    Request your free personalized quote and custom event mockup.
                  </p>
                </div>

                {/* Trust signals with larger icons and text */}
                <div className="space-y-6 pt-4">
                  <motion.div 
                    className="flex items-center space-x-4"
                    variants={itemVariants}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-xl text-text/70">{t('quoteRequest.trustSignals.pricing')}</span>
                  </motion.div>

                  <motion.div 
                    className="flex items-center space-x-4"
                    variants={itemVariants}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-xl text-text/70">{t('quoteRequest.trustSignals.shipping')}</span>
                  </motion.div>

                  <motion.div 
                    className="flex items-center space-x-4"
                    variants={itemVariants}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-xl text-text/70">{t('quoteRequest.trustSignals.delivery')}</span>
                  </motion.div>

                  <motion.div 
                    className="flex items-center space-x-4"
                    variants={itemVariants}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-xl text-text/70">{t('quoteRequest.trustSignals.bulk')}</span>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right side - Form */}
              <motion.form 
                className="space-y-5"
                variants={itemVariants}
                onSubmit={handleSubmit}
              >
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-text/70 mb-1">
                      Your Name (required)
                    </label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      type="text"
                      className="w-full px-4 py-3 rounded-lg bg-white border border-subtle 
                        focus:border-secondary focus:ring-1 focus:ring-secondary 
                        transition-colors duration-200"
                      placeholder={t('quoteRequest.form.name')}
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-text/70 mb-1">
                      Email Address (required)
                    </label>
                    <input
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      type="email"
                      className="w-full px-4 py-3 rounded-lg bg-white border border-subtle 
                        focus:border-secondary focus:ring-1 focus:ring-secondary 
                        transition-colors duration-200"
                      placeholder={t('quoteRequest.form.email')}
                      required
                    />
                  </div>

                  {/* About Event - Optional */}
                  <div>
                    <label className="block text-sm font-medium text-text/70 mb-1">
                      Tell us about your event (optional)
                    </label>
                    <textarea
                      name="requirements"
                      value={formData.requirements}
                      onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg bg-white border border-subtle 
                        focus:border-secondary focus:ring-1 focus:ring-secondary 
                        transition-colors duration-200 resize-none"
                      placeholder="Event details, attendee count, date, or any questions you have"
                    />
                  </div>

                  {/* Hidden fields to maintain compatibility */}
                  <input type="hidden" name="conferenceName" value={formData.conferenceName} />
                  <input type="hidden" name="expectedAttendees" value={formData.attendeeCount} />
                  <input type="hidden" name="eventDate" value={formData.eventDate} />

                  {/* Reassurance & Expectation Setting - More compact */}
                  <div className="bg-secondary/5 p-3 rounded-lg space-y-3">
                    <p className="font-medium text-primary flex items-start">
                      <svg className="w-5 h-5 text-secondary mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      We'll email you within 24 hours with:
                    </p>
                    <ul className="pl-7 space-y-1 text-text/80">
                      <li className="list-disc">Your personalized quote (all-inclusive pricing)</li>
                      <li className="list-disc">A free custom mockup showing exactly how BridgeOnce works for your event</li>
                    </ul>
                    
                    <div className="space-y-1 pt-1">
                      <p className="flex items-center text-text/70">
                        <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        No sales calls unless you specifically request one.
                      </p>
                      <p className="flex items-center text-text/70">
                        <svg className="w-5 h-5 text-secondary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Your information is secure and confidential.
                      </p>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit"
                    disabled={formState.submitting}
                    className="w-full bg-primary text-white py-3.5 rounded-lg 
                      hover:bg-primary-light transition-colors duration-200 
                      font-medium text-lg shadow-lg hover:shadow-xl
                      transform hover:-translate-y-0.5 transition-all 
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formState.submitting ? 'Sending...' : 'Get My Free Quote & Mockup'}
                  </button>

                  {/* Error Message */}
                  {formState.errors && (
                    <div className="text-red-500 text-sm text-center">
                      {t('quoteRequest.form.error')}
                    </div>
                  )}
                </div>
              </motion.form>
            </div>
          </motion.div>
        </div>
      </section>
      
      <SuccessPopup
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Thanks for your request!"
        message={t('quoteRequest.form.success')}
      />
    </>
  );
} 